// Home.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { apiCall, API_BASE } from '../api';
import { clearSessionFlag } from '../auth/silentReauth';
import CompleteProfile from './auth/Profile';   // ADDED
import { setI18nLanguage } from '../i18n/config';
import i18n from '../i18n/config';


const Home = ({ onSelectNgo, onCreateOrg }) => {
  const { user, logout, getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation('home');
  const { t: tc } = useTranslation('common');
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);
  const [ngos, setNgos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showProfile, setShowProfile] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);   // ADDED
  const profileRef = useRef(null);

  useEffect(() => {
    apiCall('/api/organizations/', {}, getAccessTokenSilently)
      .then(data => setNgos(data))
      .catch(() => setError(t('errorLoading')))
      .finally(() => setLoading(false));
  }, [getAccessTokenSilently, t]);
   
  // Fetch the user's preferred language from the backend and apply it —
  // but only if this device doesn't already have a saved choice. Without
  // this guard, this effect re-runs on every mount (refresh, back button)
  // and can stomp a correct localStorage/i18n value with a stale or
  // default backend value, which is what was resetting the app to English

   useEffect(() => {
    if (!getAccessTokenSilently) return;
    if (localStorage.getItem('zbookkeeping_language')) return;
    (async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
        });
        const res = await fetch(`${API_BASE || ''}/api/accounts/profile/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.preferred_language) {
            localStorage.setItem('zbookkeeping_language', data.preferred_language);
            setI18nLanguage(data.preferred_language);
          }
        }
      } catch {
        // Non-fatal: fall back to browser detection / existing localStorage choice.
      }
    })();
  }, [getAccessTokenSilently]);

  // Close the profile dropdown on outside click — same pattern used on
  // AddUser / UserList / BankDetails / BankList / AdminDashboard.
  useEffect(() => {
    if (!showProfile) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfile(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfile]);

  // Close the language menu on outside click.
  useEffect(() => {
    if (!langMenuOpen) return;
    const handleClickOutside = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [langMenuOpen]);

  const handleLogout = () => {
    setShowProfile(false);
    clearSessionFlag();
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleEditProfile = () => {
    setShowProfile(false);
    setEditingProfile(true);
  };

  // NEW: full-screen, non-blocking edit view — same form as the mandatory gate
  if (editingProfile) {
    return (
      <CompleteProfile
        onComplete={() => setEditingProfile(false)}
        onCancel={() => setEditingProfile(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3">
        <div className="flex items-center gap-2 flex-1">
          <img
            src="https://i.postimg.cc/2jPTkT7T/z-bookkeeping.png"
            alt="logo"
            className="w-8 h-8 object-contain"
          />
          <span className="text-sm font-medium text-gray-900">z.Bookkeeping</span>
        </div>

        {/* ---- LANGUAGE TOGGLE ---- */}
        <div ref={langMenuRef} className="relative">
          <button
            onClick={() => setLangMenuOpen((open) => !open)}
            aria-label="Language menu"
            aria-expanded={langMenuOpen}
            className="text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition"
          >
            {i18n.language === 'ml' ? 'മലയാളം' : 'EN'}
          </button>
          {langMenuOpen && (
            <div className="absolute right-0 top-10 w-32 bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">
              <button
                onClick={() => {
                  const lang = 'en';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${window.location.origin}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'en' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => {
                  const lang = 'ml';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${window.location.origin}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'ml' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                മലയാളം
              </button>
            </div>
          )}
        </div>

        {/* ---- USER AVATAR + PROFILE DROPDOWN ---- */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setShowProfile((open) => !open)}
            aria-label="Account menu"
            aria-expanded={showProfile}
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium"
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </button>

          {showProfile && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">
              <p className="text-sm text-gray-900 truncate">{user?.email}</p>
              <p className="text-sm text-gray-400 truncate mb-4">{user?.email}</p>

              <button
                onClick={handleEditProfile}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition mb-2"
              >
                {tc('editProfile')}
              </button>
              <button
                onClick={handleLogout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {tc('logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-sm mx-auto px-4 py-8">
        <h1 className="text-center text-base font-medium text-blue-600 mb-6 uppercase tracking-wider">
          {t('title')}
        </h1>

        {/* Loading / Error states */}
        {loading && (
          <p className="text-sm text-gray-400 text-center mb-6">{t('loadingNgos')}</p>
        )}
        {!loading && error && (
          <p className="text-sm text-red-500 text-center mb-6">{error}</p>
        )}
        {!loading && !error && ngos.length === 0 && (
          <p className="text-sm text-gray-400 text-center mb-6">
            {t('emptyState')}
          </p>
        )}

        {/* NGO List */}
        {!loading && !error && ngos.length > 0 && (
          <div className="flex flex-col gap-3 mb-6">
            {ngos.map(ngo => (
              <button
                key={ngo.id}
                onClick={() => onSelectNgo(ngo)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-4 text-sm font-medium transition text-center"
              >
                {ngo.name}
              </button>
            ))}
          </div>
        )}

        {/* Divider */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 h-px bg-gray-200"></div>
          <span className="text-xs text-gray-400">{t('or')}</span>
          <div className="flex-1 h-px bg-gray-200"></div>
        </div>

        {/* Create New */}
        <button
          onClick={onCreateOrg}
          className="w-full border-2 border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg px-4 py-4 text-sm font-medium transition text-center"
        >
          {t('createNewOrganization')}
        </button>
      </div>
    </div>
  );
};

export default Home;